import { useEffect, useMemo, useState } from "react";
import {
  addPenCustomColor,
  loadPenCustomColors,
  removePenCustomColor,
  savePenColor,
  updatePenCustomColor,
} from "../../../lib/pen-preference";
import {
  addStickyCustomColor,
  loadStickyCustomColors,
  removeStickyCustomColor,
  saveStickyColor,
  updateStickyCustomColor,
} from "../../../lib/sticky-preference";
import {
  addShapeStrokeCustomColor,
  addShapeCustomColor,
  loadShapeCustomColors,
  loadShapeStrokeCustomColors,
  removeShapeCustomColor,
  removeShapeStrokeCustomColor,
  saveShapeFill,
  saveShapeStroke,
  updateShapeCustomColor,
  updateShapeStrokeCustomColor,
} from "../../../lib/shape-preference";
import {
  addSectionCustomColor,
  loadSectionCustomColors,
  removeSectionCustomColor,
  updateSectionCustomColor,
} from "../../../lib/section-preference";
import { isValidHexColor, normalizeHexColor } from "../../../lib/hex-color";
import { useCanvasStore } from "../store/canvas-store";

export interface CustomColorPalette {
  readonly colors: readonly string[];
  readonly add: (color: string) => void;
  readonly update: (previous: string, next: string) => void;
  readonly remove: (color: string) => void;
}

export interface CustomColorPalettes {
  readonly pen: CustomColorPalette;
  readonly sticky: CustomColorPalette;
  readonly shape: CustomColorPalette;
  readonly shapeStroke: CustomColorPalette;
  readonly section: CustomColorPalette;
}

function sameHex(left: string, right: string): boolean {
  return (
    isValidHexColor(left) &&
    isValidHexColor(right) &&
    normalizeHexColor(left) === normalizeHexColor(right)
  );
}

/** 5 種類のカスタム色を、表示 state と永続操作を一体にして提供する。 */
export function useCustomColorPalettes(): CustomColorPalettes {
  const [penColors, setPenColors] = useState<readonly string[]>(loadPenCustomColors);
  const [stickyColors, setStickyColors] = useState<readonly string[]>(loadStickyCustomColors);
  const [shapeColors, setShapeColors] = useState<readonly string[]>(loadShapeCustomColors);
  const [shapeStrokeColors, setShapeStrokeColors] = useState<readonly string[]>(
    loadShapeStrokeCustomColors
  );
  const [sectionColors, setSectionColors] = useState<readonly string[]>(loadSectionCustomColors);

  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key === null || event.key === "refinement-canvas:pen:custom-colors") {
        setPenColors(loadPenCustomColors());
      }
      if (event.key === null || event.key === "refinement-canvas:sticky:custom-colors") {
        setStickyColors(loadStickyCustomColors());
      }
      if (event.key === null || event.key === "refinement-canvas:shape:custom-colors") {
        setShapeColors(loadShapeCustomColors());
      }
      if (event.key === null || event.key === "refinement-canvas:shape:stroke-custom-colors") {
        setShapeStrokeColors(loadShapeStrokeCustomColors());
      }
      if (event.key === null || event.key === "refinement-canvas:section:custom-colors") {
        setSectionColors(loadSectionCustomColors());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const pen = useMemo<CustomColorPalette>(
    () => ({
      colors: penColors,
      add: (color) => setPenColors(addPenCustomColor(color)),
      update: (previous, next) => {
        const result = updatePenCustomColor(previous, next);
        setPenColors(result.colors);
        if (result.applied && sameHex(useCanvasStore.getState().penColor, previous)) {
          const normalized = normalizeHexColor(next);
          useCanvasStore.getState().setPenColor(normalized);
          savePenColor(normalized);
        }
      },
      remove: (color) => setPenColors(removePenCustomColor(color)),
    }),
    [penColors]
  );

  const sticky = useMemo<CustomColorPalette>(
    () => ({
      colors: stickyColors,
      add: (color) => setStickyColors(addStickyCustomColor(color)),
      update: (previous, next) => {
        const result = updateStickyCustomColor(previous, next);
        setStickyColors(result.colors);
        if (result.applied && sameHex(useCanvasStore.getState().stickyColor, previous)) {
          const normalized = normalizeHexColor(next);
          useCanvasStore.getState().setStickyColor(normalized);
          saveStickyColor(normalized);
        }
      },
      remove: (color) => setStickyColors(removeStickyCustomColor(color)),
    }),
    [stickyColors]
  );

  const shape = useMemo<CustomColorPalette>(
    () => ({
      colors: shapeColors,
      add: (color) => setShapeColors(addShapeCustomColor(color)),
      update: (previous, next) => {
        const result = updateShapeCustomColor(previous, next);
        setShapeColors(result.colors);
        if (result.applied && sameHex(useCanvasStore.getState().shapeFill, previous)) {
          const normalized = normalizeHexColor(next);
          useCanvasStore.getState().setShapeFill(normalized);
          saveShapeFill(normalized);
        }
      },
      remove: (color) => setShapeColors(removeShapeCustomColor(color)),
    }),
    [shapeColors]
  );

  const section = useMemo<CustomColorPalette>(
    () => ({
      colors: sectionColors,
      add: (color) => setSectionColors(addSectionCustomColor(color)),
      update: (previous, next) => {
        const result = updateSectionCustomColor(previous, next);
        setSectionColors(result.colors);
        if (result.applied && sameHex(useCanvasStore.getState().sectionFill, previous)) {
          useCanvasStore.getState().setSectionFill(normalizeHexColor(next));
        }
      },
      remove: (color) => setSectionColors(removeSectionCustomColor(color)),
    }),
    [sectionColors]
  );

  const shapeStroke = useMemo<CustomColorPalette>(
    () => ({
      colors: shapeStrokeColors,
      add: (color) => setShapeStrokeColors(addShapeStrokeCustomColor(color)),
      update: (previous, next) => {
        const result = updateShapeStrokeCustomColor(previous, next);
        setShapeStrokeColors(result.colors);
        if (result.applied && sameHex(useCanvasStore.getState().shapeStroke, previous)) {
          const normalized = normalizeHexColor(next);
          useCanvasStore.getState().setShapeStroke(normalized);
          saveShapeStroke(normalized);
        }
      },
      remove: (color) => setShapeStrokeColors(removeShapeStrokeCustomColor(color)),
    }),
    [shapeStrokeColors]
  );

  return useMemo(
    () => ({ pen, sticky, shape, shapeStroke, section }),
    [pen, section, shape, shapeStroke, sticky]
  );
}
